import React, { useCallback, useMemo } from 'react';

import { Icon } from 'js/components';
import { faCirclePlus, faEmptySet } from '@fortawesome/pro-regular-svg-icons';
import { Input } from 'reactstrap';

import AnswerSetSelector from 'js/components/Assessment/Editors/common/components/AnswerSets/AnswerSetSelector';
import { useAnswerSetContext } from 'js/components/Assessment/Editors/common/components/AnswerSets/AnswerSetContext';
import { useStandardCollator } from 'js/utils/string';
import { Checkbox } from 'js/components/Forms/Checkbox';
import useEditorInput from 'js/components/Assessment/Editors/common/hooks/useEditorInput';

import {
  MIN_SENTIMENT_OPTIONS,
  MAX_SENTIMENT_OPTIONS,
  decorationFor,
  decorationForQuestion,
} from 'js/utils/sentiment-question';
import { canReorderSentimentOptions } from 'js/components/Assessment/Editors/common/binding/sentiment-actions';
import DecorationSelector from './DecorationSelector';
import SentimentOption from './SentimentOption';

import styles from './Sentiment.module.scss';

/**
 * The `ChoiceEditor` for `sentiment`, injected through
 * `AnswerSetContextProvider`.
 *
 * Options are a vertical list, lowest value first. Reordering is drag-and-drop
 * within this question only: the drop edges are top/bottom (the same as
 * questions), and a successful drop reassigns values so the new order *is*
 * the scale. Decoration is derived from that rank, not authored per option.
 */
export function SentimentOptions({
  answerSet,
  question,
  questionId,
  questionType,
  onAddChoice = () => {},
  onDeleteChoice = () => {},
  onChoiceUpdate = () => {},
  onChoiceMove = () => {},
  onAnswerSetChange = () => {},
  onFieldChange = () => {},
  idAttribute = 'guid',
}) {
  const collator = useStandardCollator();
  const { isValidAnswerSetTypeForQuestion, onDecorationChange = () => {} } =
    useAnswerSetContext();
  const decoration = decorationForQuestion(question);
  const answerSetId = answerSet?.[idAttribute];
  const canReorder = canReorderSentimentOptions(answerSet);
  const isStandard = answerSet?.isStandard ?? false;

  const isValidAnswerSet = isValidAnswerSetTypeForQuestion(
    questionType,
    answerSet?.type
  );

  // Position, not value, is the source of truth in the editor: a drop rewrites
  // `p` first and then renumbers values to match. Sorting by `p` is therefore
  // what the drag targets themselves are indexed against.
  const options = useMemo(() => {
    if (!isValidAnswerSet) {
      return [];
    }

    const ranked = Object.values(answerSet?.choices ?? {})
      .filter((choice) => !choice.is_na)
      .sort((a, b) => collator.compare(a.p, b.p));

    return ranked.map((choice, index) => ({
      choice,
      ...decorationFor(index, ranked.length, decoration),
    }));
  }, [answerSet?.choices, isValidAnswerSet, decoration, collator]);

  const canAdd = options.length < MAX_SENTIMENT_OPTIONS;
  const canRemove = options.length > MIN_SENTIMENT_OPTIONS;

  const handleAdd = useCallback(() => {
    onAddChoice({ text: `Option ${options.length + 1}` });
  }, [onAddChoice, options.length]);

  const handleMove = useCallback(
    (sourceChoiceId, targetChoiceId, placeAfter) => {
      onChoiceMove(answerSetId, sourceChoiceId, targetChoiceId, placeAfter);
    },
    [onChoiceMove, answerSetId]
  );

  const handleIncludeNAChange = useCallback(
    (e) => {
      if (!answerSetId) {
        return;
      }
      onFieldChange(answerSetId, { includeNA: e.target.checked });
    },
    [answerSetId, onFieldChange]
  );

  const { ref: naTextRef, ...naTextInputProps } = useEditorInput({
    value: answerSet?.naText,
    onCommit: (naText) => {
      if (answerSetId) {
        onFieldChange(answerSetId, { naText });
      }
    },
  });

  return (
    <div className={styles.options}>
      <div className={styles['options-header']}>
        <div>
          <DecorationSelector
            decoration={decoration}
            onChange={(style) => onDecorationChange(questionId, style)}
          />
        </div>
      </div>
      <div data-sentiment-options={questionId}>
        {options.map(
          ({ choice, icon, rotate, shrink, color, ink, colorOnly }, index) => (
            <SentimentOption
              key={choice.guid ?? choice.id}
              choice={choice}
              index={index}
              questionId={questionId}
              answerSetId={answerSetId}
              icon={icon}
              rotate={rotate}
              shrink={shrink}
              color={color}
              ink={ink}
              colorOnly={colorOnly}
              canReorder={canReorder}
              canRemove={canRemove}
              onMove={handleMove}
              onChoiceUpdate={onChoiceUpdate}
              onDeleteChoice={onDeleteChoice}
            />
          )
        )}
        {answerSet?.includeNA ? (
          <div
            className={`${styles.option} ${styles['option-static']} ${styles['option-na']}`}
          >
            <div className={styles.decoration} aria-hidden="true">
              <Icon icon={faEmptySet} />
            </div>
            <div className={styles['option-text']}>
              {answerSet.naText || 'N/A'}
            </div>
          </div>
        ) : null}
      </div>
      <div className={styles['add-choice']}>
        <button
          type="button"
          className="btn btn-light"
          disabled={!canAdd}
          title={
            canAdd
              ? 'Add a choice'
              : `${MAX_SENTIMENT_OPTIONS} choices is the recommended maximum`
          }
          onClick={handleAdd}
        >
          <Icon icon={faCirclePlus} className="icon" size="lg" />
          <span>Add choice</span>
        </button>
      </div>
      {!isStandard && isValidAnswerSet ? (
        <div className={styles['na-option']}>
          <Checkbox
            onChange={handleIncludeNAChange}
            checked={!!answerSet?.includeNA}
          >
            Enable a &quot;Not Applicable&quot; option
          </Checkbox>
          {answerSet?.includeNA ? (
            <>
              <label htmlFor={`${answerSetId}-na-text`}>
                Rename the &quot;Not Applicable&quot; option
              </label>
              <Input
                type="text"
                id={`${answerSetId}-na-text`}
                innerRef={naTextRef}
                {...naTextInputProps}
              />
            </>
          ) : null}
        </div>
      ) : null}
      {options.length === 0 && (
        <div className={styles['choose-answer-set']}>
          <p>Choose an existing answer set</p>
          <AnswerSetSelector
            currentAnswerSet={answerSet}
            questionType={questionType}
            onChange={onAnswerSetChange}
          />
        </div>
      )}
    </div>
  );
}

export default SentimentOptions;
